import { reactive } from 'vue'
import { useRoute } from 'vue-router'
import dayjs from 'dayjs'

import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { eventTypeEnum, useScheduleStore } from '@/stores/schedule.store'
import { useAbsenceCreate } from '@/composables/absence-create'
import { useEntityChanges } from '@/composables/entity-changes'
import { entityChangeTypeEnum } from '@/stores/portal.store'

const DATE_FORMAT = 'YYYY-MM-DD'
const toIso = (value) => dayjs(value).toISOString()

export const includesQuery = (text, query) => !!text?.toLowerCase().includes(query)

export function useTimelineFocus() {
  const route = useRoute()
  const webSiteStore = useWebSiteStore()
  const routeFocus = route.query.projectId && route.query.serviceId
    ? { projectId: Number(route.query.projectId), serviceId: Number(route.query.serviceId), date: route.query.date ?? null }
    : null
  const storeFocus = webSiteStore.editedProjectId
    ? { projectId: webSiteStore.editedProjectId, serviceId: null, date: webSiteStore.timelineFocusDate }
    : null
  webSiteStore.editedProjectId = null
  webSiteStore.timelineFocusDate = null
  return routeFocus ?? storeFocus
}

export function useServiceSearch() {
  const planningStore = usePlanningStore()

  const loadServicesAsync = () => Promise.all([
    planningStore.fetchStaffsAsync({ all: true }, true),
    planningStore.fetchEquipmentsAsync({ all: true }, true),
    planningStore.fetchGroupsAsync({ all: true }, true),
  ])

  // `row` is any `{ service_id, listing_item_text }` budget row; `query` is already lower-cased.
  const searchMatchLabel = ({ service_id: serviceId, listing_item_text: listingItemText }, query) => {
    const service = planningStore.getServiceById(serviceId)
    const serviceName = planningStore.getServiceName(service)
    const firstNameFirst = service?.first_name ? `${service.first_name} ${service.name}` : null
    if (includesQuery(serviceName, query) || includesQuery(firstNameFirst, query)) return serviceName
    return includesQuery(listingItemText, query) ? listingItemText : null
  }

  const matchesServiceSearch = (row, query) => !!searchMatchLabel(row, query)

  const serviceSearchMatchLabels = (rows, query) => [...new Set(rows.map(row => searchMatchLabel(row, query)).filter(Boolean))]

  return { loadServicesAsync, matchesServiceSearch, serviceSearchMatchLabels }
}

// Data source of ProjectTimeline: the `events/timeline/` snapshot of the loaded window, and every
// change sent to the existing schedule/planning endpoints, followed by a reload of that window.
export function useProjectTimelineData() {
  const planningStore = usePlanningStore()
  const scheduleStore = useScheduleStore()
  const { conflictDialog: absenceConflictDialog, saveAbsenceAsync } = useAbsenceCreate()
  const { loadServicesAsync } = useServiceSearch()

  const state = reactive({ projects: [], rows: [], events: [], loading: false })
  let loadedWindow = null
  let staticDataLoaded = null

  const loadStaticDataAsync = () => {
    staticDataLoaded ??= Promise.all([
      loadServicesAsync(),
      planningStore.fetchListingsAsync({ all: true }, true),
    ])
    return staticDataLoaded
  }

  const codeLessName = (service) => (service?.first_name ? `${service.name} ${service.first_name}` : service?.name ?? '')

  const withService = (row) => {
    const service = planningStore.getServiceById(row.service_id)
    return {
      ...row,
      service_type: service?.type ?? null,
      service_name: codeLessName(service),
      service_title: service ? planningStore.getServiceName(service) : `#${row.service_id}`,
      service_avatar: service?.avatar_file || service?.avatar_url || null,
    }
  }

  let latestLoad = 0

  const loadAsync = async(start, end, projectIds = null) => {
    const load = ++latestLoad
    loadedWindow = { start, end, projectIds }
    state.loading = true
    try {
      await loadStaticDataAsync()
      const { data } = await scheduleStore.fetchTimelineAsync(toIso(start), toIso(end), projectIds)
      if (load !== latestLoad) return
      state.projects = data.projects
      state.rows = data.rows.map(withService)
      state.events = data.events
    } finally {
      if (load === latestLoad) state.loading = false
    }
  }

  const reloadAsync = () => (loadedWindow ? loadAsync(loadedWindow.start, loadedWindow.end, loadedWindow.projectIds) : Promise.resolve())

  const refreshAsync = () => {
    staticDataLoaded = null
    return reloadAsync()
  }

  useEntityChanges([entityChangeTypeEnum.EVENT], reloadAsync)

  const projectById = (projectId) => state.projects.find(project => project.id === projectId)
  const updatePlanned = (projectId) => !projectById(projectId)?.lock_planned

  const changeRowRangeAsync = async(row, { start, end }, shift) => {
    await scheduleStore.updateRangeAsync(row.project_id, toIso(start), toIso(end), {
      budgetDocumentRowId: row.id,
      updatePlanned: updatePlanned(row.project_id),
      shift,
    })
    await reloadAsync()
  }

  const saveEventAsync = async(event) => {
    if (event.type === eventTypeEnum.ABSENCE && !event.id) {
      await saveAbsenceAsync(event.service_id, toIso(event.start), toIso(event.end), reloadAsync)
      return
    }
    await scheduleStore.upsertEventAsync({
      ...(event.id
        ? { id: event.id }
        : { service: event.service_id, project: event.project_id, budget_document_row: event.budget_document_row_id }),
      start: toIso(event.start),
      end: toIso(event.end),
      ...(event.type === eventTypeEnum.ABSENCE ? { confirmed: event.confirmed } : { update_planned: updatePlanned(event.project_id) }),
    })
    await reloadAsync()
  }

  const setAbsenceApprovalAsync = async(absence, approved) => {
    await scheduleStore.upsertEventAsync({ id: absence.id, confirmed: approved })
    await reloadAsync()
  }

  const deleteEventAsync = async(event) => {
    await scheduleStore.deleteEventAsync(event.id, { updatePlanned: event.project_id ? updatePlanned(event.project_id) : false })
    await reloadAsync()
  }

  const deleteRowAsync = async(row) => {
    await scheduleStore.deleteRangeAsync(row.id, { updatePlanned: updatePlanned(row.project_id) })
    await reloadAsync()
  }

  const deleteProjectAllocationsAsync = async(project) => {
    await scheduleStore.deleteRangeAsync(null, { projectId: project.id, updatePlanned: updatePlanned(project.id) })
    await reloadAsync()
  }

  const updateProjectAsync = async(project, changes) => {
    await planningStore.upsertProjectAsync({ id: project.id, ...changes })
    await reloadAsync()
  }

  const assignServiceAsync = async(project, service, { quantity = 1, selectedComponentIds = null } = {}) => {
    const { data } = await scheduleStore.syncRangeAsync(service.id, project.id, toIso(project.start), toIso(project.end), {
      quantity,
      listingId: project.listing_id,
      selectedComponentIds,
    })
    await reloadAsync()
    return data.budget_document_row_id
  }

  const replaceServiceAsync = async(row, serviceId) => {
    const { data } = await scheduleStore.replaceServiceAsync(row.id, serviceId)
    await reloadAsync()
    return data.budget_document_row_id
  }

  const cancelAllocationAsync = async(serviceId, projectId, budgetDocumentRowId = null) => {
    await scheduleStore.cancelAllocationAsync(serviceId, projectId, budgetDocumentRowId)
    await reloadAsync()
  }

  // --- Conflict resolution helpers ---
  const atDayOf = (day, iso) => {
    const time = dayjs(iso)
    return day.hour(time.hour()).minute(time.minute()).second(0).millisecond(0)
  }

  const firstFreeDayAfter = (allocation, absence) => {
    const busyDays = new Set(state.events
      .filter(ev => ev.id !== allocation.id && ev.budget_document_row_id === allocation.budget_document_row_id)
      .map(ev => dayjs(ev.start).format(DATE_FORMAT)))
    let day = dayjs(absence.end).add(1, 'day').startOf('day')
    while (busyDays.has(day.format(DATE_FORMAT))) day = day.add(1, 'day')
    return day
  }

  const allocationAfterAbsence = (allocation, absence) => {
    const day = firstFreeDayAfter(allocation, absence)
    return { ...allocation, start: toIso(atDayOf(day, allocation.start)), end: toIso(atDayOf(day, allocation.end)) }
  }

  const absenceSpansWithout = (absence, allocation) => {
    const removed = new Set()
    for (let day = dayjs(allocation.start).startOf('day'); !day.isAfter(dayjs(allocation.end), 'day'); day = day.add(1, 'day')) {
      removed.add(day.format(DATE_FORMAT))
    }
    const spans = []
    for (let day = dayjs(absence.start).startOf('day'); !day.isAfter(dayjs(absence.end), 'day'); day = day.add(1, 'day')) {
      if (removed.has(day.format(DATE_FORMAT))) continue
      const last = spans[spans.length - 1]
      if (last && last.end.add(1, 'day').isSame(day, 'day')) last.end = day
      else spans.push({ start: day, end: day })
    }
    return spans.map(span => ({ start: toIso(atDayOf(span.start, absence.start)), end: toIso(atDayOf(span.end, absence.end)) }))
  }

  const trimAbsenceAsync = async(absence, allocation) => {
    const [first, ...others] = absenceSpansWithout(absence, allocation)
    if (!first) {
      await deleteEventAsync(absence)
      return
    }
    await scheduleStore.upsertEventAsync({ id: absence.id, ...first })
    await Promise.all(others.map(span => saveAbsenceAsync(absence.service_id, span.start, span.end)))
    await reloadAsync()
  }

  return {
    state,
    absenceConflictDialog,
    loadAsync,
    reloadAsync,
    refreshAsync,
    changeRowRangeAsync,
    saveEventAsync,
    setAbsenceApprovalAsync,
    deleteEventAsync,
    deleteRowAsync,
    deleteProjectAllocationsAsync,
    updateProjectAsync,
    assignServiceAsync,
    replaceServiceAsync,
    cancelAllocationAsync,
    allocationAfterAbsence,
    trimAbsenceAsync,
  }
}
